import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface AiWeltArea {
  id: number;
  name: string;
  slug?: string;
  color: string;
  description?: string | null;
  sort_order?: number;
  is_active?: boolean;
  grid_x: number;
  grid_z: number;
  cols: number;
  rows: number;
  user_ids?: number[];
  assignments?: Array<{ user_id: number; desk_slot: number }>;
}

export interface AiWeltDeskUser {
  desk_index: number;
  user_id: number;
  name: string;
  short?: string;
  firstname?: string;
  role?: string;
  area_id?: number | null;
  avatar_style?: 'auto' | 'female' | 'male' | 'neutral' | string;
  online: boolean;
  route?: string | null;
  route_label?: string | null;
  order_id?: number | null;
  order_number?: string | null;
  order_label?: string | null;
  activity?: string;
  last_activity_at?: string | null;
  activity_burst_at?: string | null;
}

export interface AiWeltOfficeSnapshot {
  users: AiWeltDeskUser[];
  areas?: AiWeltArea[];
  online_count: number;
  server_time?: string;
  openai_configured?: boolean;
  chats_ready?: boolean;
  areas_ready?: boolean;
}

export interface AiWeltThread {
  id: number;
  type: 'internal' | 'chatgpt' | string;
  user_id?: number | null;
  created_by?: number | null;
  title?: string;
  last_message_at?: string | null;
}

export interface AiWeltMessage {
  id: number;
  thread_id: number;
  sender_id?: number | null;
  role: string;
  body: string;
  is_read?: boolean;
  created_at?: string;
}

export interface AiWeltActivityUserSeries {
  user_id: number;
  name: string;
  firstname?: string | null;
  short?: string | null;
  hours: number[];
  active_minutes_total: number;
  mouse_events?: number;
  typing_events?: number;
  focus_events?: number;
}

export interface AiWeltActivityTimeline {
  date: string;
  hours: number[];
  totals: number[];
  by_user: AiWeltActivityUserSeries[];
  activity_ready: boolean;
  migrate_hint?: string | null;
}

export interface AiWeltDayStatsSummary {
  active_minutes: number;
  appointments_created: number;
  appointments_confirmed: number;
  api_syncs: number;
  mails_sent: number;
  appointment_requests: number;
  phone_calls: number;
  phone_duration_seconds: number;
  logins: number;
  timeline_count: number;
}

export interface AiWeltDayStatsTimelineItem {
  type: string;
  title: string;
  at: string;
  time: string;
  meta?: Record<string, any>;
}

export interface AiWeltUserDayStats {
  user_id: number;
  user: { id: number; name: string; firstname?: string | null; short?: string | null };
  date: string;
  prev_date: string;
  next_date: string;
  is_today: boolean;
  summary: AiWeltDayStatsSummary;
  activity: {
    hours: number[];
    active_minutes_total: number;
    mouse_events: number;
    typing_events: number;
    focus_events: number;
  };
  evaluation: { headline: string; text: string; tags: string[] };
  timeline: AiWeltDayStatsTimelineItem[];
}

export interface AiWeltCallStatsSummary {
  total: number;
  inbound: number;
  outbound: number;
  missed: number;
  answered: number;
  duration_seconds: number;
  inbound_duration_seconds: number;
  outbound_duration_seconds: number;
}

export interface AiWeltCallStatsItem {
  id: string;
  at: string;
  time: string;
  date: string;
  direction: 'inbound' | 'outbound' | 'unknown' | string;
  status?: string | null;
  missed: boolean;
  duration_seconds: number;
  duration_label: string;
  peer_number: string;
  accepted_by?: string | null;
  sipgate_user_id?: string | null;
  type?: string;
  order_id?: number | null;
  order_number?: string | null;
  customer_id?: number | null;
  customer_name?: string | null;
  customer_short?: string | null;
  person_name?: string | null;
  person_last_name?: string | null;
  match_label?: string | null;
}

export interface AiWeltUserCallStats {
  user_id: number;
  user: { id: number; name: string; firstname?: string | null; short?: string | null; email?: string | null };
  from: string;
  to: string;
  sipgate_mapping: {
    matched: boolean;
    sipgate_user_ids: string[];
    sipgate_names: string[];
    match_hints: string[];
  };
  summary: AiWeltCallStatsSummary;
  evaluation: { headline: string; text: string; tags: string[] };
  calls: AiWeltCallStatsItem[];
  error?: string | null;
}

@Injectable({ providedIn: 'root' })
export class AiWeltService {
  private readonly base = environment.prismaApiUrl + '/api/dispo/stats/ai-welt';

  constructor(private http: HttpClient) {}

  postPresence(payload: {
    route?: string;
    route_label?: string;
    order_id?: number | null;
    order_label?: string;
    activity?: 'idle' | 'mouse' | 'typing' | 'focus';
  }): Observable<{ ok: boolean }> {
    return this.http.post<{ ok: boolean }>(this.base + '/presence', payload);
  }

  getOffice(): Observable<AiWeltOfficeSnapshot> {
    return this.http.get<AiWeltOfficeSnapshot>(this.base + '/office');
  }

  getActivity(params?: { date?: string; user_id?: number }): Observable<AiWeltActivityTimeline> {
    const q: string[] = [];
    if (params?.date) {
      q.push('date=' + encodeURIComponent(params.date));
    }
    if (params?.user_id) {
      q.push('user_id=' + params.user_id);
    }
    const qs = q.length ? '?' + q.join('&') : '';
    return this.http.get<AiWeltActivityTimeline>(this.base + '/activity' + qs);
  }

  getUserDayStats(userId: number, date?: string): Observable<AiWeltUserDayStats> {
    const q = ['user_id=' + userId];
    if (date) {
      q.push('date=' + encodeURIComponent(date));
    }
    return this.http.get<AiWeltUserDayStats>(this.base + '/user-day-stats?' + q.join('&'));
  }

  getUserCallStats(userId: number, from?: string, to?: string): Observable<AiWeltUserCallStats> {
    const q = ['user_id=' + userId];
    if (from) {
      q.push('from=' + encodeURIComponent(from));
    }
    if (to) {
      q.push('to=' + encodeURIComponent(to));
    }
    return this.http.get<AiWeltUserCallStats>(this.base + '/user-call-stats?' + q.join('&'));
  }

  getAreasAdmin(): Observable<{
    areas: AiWeltArea[];
    users: Array<{
      id: number;
      name: string;
      short?: string;
      firstname?: string;
      area_id?: number | null;
      avatar_style?: string;
    }>;
    areas_ready?: boolean;
    migrate_hint?: string | null;
  }> {
    return this.http.get<any>(this.base + '/areas');
  }

  createArea(body: Partial<AiWeltArea>): Observable<{ area: AiWeltArea }> {
    return this.http.post<{ area: AiWeltArea }>(this.base + '/areas', body);
  }

  updateArea(id: number, body: Partial<AiWeltArea>): Observable<{ area: AiWeltArea }> {
    return this.http.put<{ area: AiWeltArea }>(this.base + '/areas/' + id, body);
  }

  deleteArea(id: number): Observable<{ ok: boolean }> {
    return this.http.delete<{ ok: boolean }>(this.base + '/areas/' + id);
  }

  assignUser(userId: number, areaId: number | null, deskSlot?: number): Observable<any> {
    return this.http.put(this.base + '/assignments', {
      user_id: userId,
      area_id: areaId,
      desk_slot: deskSlot,
    });
  }

  setAvatarStyle(userId: number, avatarStyle: string): Observable<any> {
    return this.http.put(this.base + '/avatar', {
      user_id: userId,
      avatar_style: avatarStyle,
    });
  }

  getThreads(params?: { user_id?: number; type?: string }): Observable<{ threads: AiWeltThread[] }> {
    const q: string[] = [];
    if (params?.user_id) {
      q.push('user_id=' + params.user_id);
    }
    if (params?.type) {
      q.push('type=' + encodeURIComponent(params.type));
    }
    const qs = q.length ? '?' + q.join('&') : '';
    return this.http.get<{ threads: AiWeltThread[] }>(this.base + '/threads' + qs);
  }

  getThread(id: number): Observable<{ thread: AiWeltThread; messages: AiWeltMessage[] }> {
    return this.http.get<{ thread: AiWeltThread; messages: AiWeltMessage[] }>(this.base + '/threads/' + id);
  }

  sendMessage(body: {
    thread_id?: number;
    user_id?: number;
    type?: 'internal' | 'chatgpt';
    message: string;
    title?: string;
    agent_id?: number;
  }): Observable<{ thread: AiWeltThread; messages: AiWeltMessage[] }> {
    return this.http.post<{ thread: AiWeltThread; messages: AiWeltMessage[] }>(this.base + '/messages', body);
  }

  getUnread(): Observable<{ count: number; threads: Array<{ thread_id: number; count: number; preview: string }> }> {
    return this.http.get<{ count: number; threads: Array<{ thread_id: number; count: number; preview: string }> }>(
      this.base + '/unread'
    );
  }
}
